import { DateHelper, EnumHelper, FileHelper, LocalizationHelperInstance } from "utils/helpers";
import { AuthEnums, StyleEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { InvoiceFormTypes, QuotesFormTypes } from "models/formTypes";
import useAPIClientHelper from "./useAPIClientHelper";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";

function useQuotesService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getQuotes = async (pagination: WintUtilsWebApi.Filter, filter?: QuotesFormTypes.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).QuotationClient.GetByFilter({
      ...pagination,
      ...reportToAPIFilter(filter),
    });
  };

  const getStateText = (state: ApiModels.QuotationApiState) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.QuoteState_${state}`);
  };

  const reportToAPIFilter = (
    formFilter?: QuotesFormTypes.Filter
  ): Omit<ApiModels.QuotationsApiFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      serialNumberStart: formFilter?.serialNumber?.lowest,
      serialNumberEnd: formFilter?.serialNumber?.highest,
      customerId: formFilter?.customerId,
      ownerId: formFilter?.ownerId,
      fromDate: formFilter?.date?.start,
      toDate: formFilter?.date?.end,
      fromEndDate: formFilter?.endDate?.start,
      toEndDate: formFilter?.endDate?.end,
      minTotalAmount: formFilter?.amount?.lowest,
      maxTotalAmount: formFilter?.amount?.highest,
      states: formFilter?.states?.map((s) => s.id as ApiModels.QuotationApiState) ?? [],
    };
  };

  const get = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .QuotationClient.GetById(id)
      .then((res) => res.data);
  };

  const getCopiedQuotation = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .QuotationClient.GetCopiedQuotation(invoiceId)
      .then((res) => res.data);
  };

  const generateQuotationPreview = (quotation: ApiModels.QuotationPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "text",
    }).QuotationClient.PreviewQuotationPdf(quotation);
  };

  const getQuotationPDFPreview = async (serialNumber: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "text",
    }).QuotationClient.PreviewInlineQuotationPdf(serialNumber);
  };

  const getUIDisplayState = (quotation?: ApiModels.Quotation) => {
    switch (quotation?.quotationState) {
      case ApiModels.QuotationApiState.InvoiceCreated:
        return StyleEnums.DisplayType.Success;
      case ApiModels.QuotationApiState.Lost:
      case ApiModels.QuotationApiState.Expired:
        return StyleEnums.DisplayType.Danger;
      case ApiModels.QuotationApiState.Cancelled:
        return StyleEnums.DisplayType.None;
      default:
        return StyleEnums.DisplayType.Info;
    }
  };

  const checkQuotationExpired = (quotation: ApiModels.Quotation) => {
    return (
      quotation.quotationState === ApiModels.QuotationApiState.Expired ||
      (quotation.endDate &&
        DateHelper.addDays(quotation.endDate, 1) < new Date() &&
        quotation.quotationState === ApiModels.QuotationApiState.Sent)
    );
  };

  const updateDimensions = (serialNumber: number, dimensions: string[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .QuotationClient.UpdateDimensions(serialNumber, {
        dimensionList: dimensions,
      })
      .then((res) => res.data);
  };

  const getQuotationDimensions = (values: any): string[] => {
    return FormFieldConstants.Quote.Details.Dimensions in values
      ? values.dimensions.map((dimension: any): ApiModels.DimensionData => dimension.dimensionId)
      : [];
  };

  const getDisplayState = (quotation: ApiModels.Quotation) => {
    if (checkQuotationExpired(quotation))
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Overdue);

    switch (quotation.quotationState) {
      case ApiModels.QuotationApiState.NotSent:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Draft);
      case ApiModels.QuotationApiState.Sent:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Sent);
      case ApiModels.QuotationApiState.Won:
      case ApiModels.QuotationApiState.InvoiceCreated:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Won);
      case ApiModels.QuotationApiState.Cancelled:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.Removed);
      case ApiModels.QuotationApiState.Expired:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Overdue);
    }
  };

  const getInvoiceTemplateFromQuotation = (serialNumber: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .QuotationClient.CreateInvoice(serialNumber)
      .then((res) => res.data);
  };

  const createQuotation = (quotationPost: ApiModels.QuotationPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .QuotationClient.Post(quotationPost)
      .then((res) => res.data);
  };

  const updateQuotation = (serialNumber: number, quotation: ApiModels.QuotationPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).QuotationClient.Edit(
      serialNumber,
      quotation
    );
  };

  const markAsSent = async (serialNumber: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).QuotationClient.MarkAsSent(serialNumber);
  };

  const downloadPdf = async (serialNumber: number) => {
    const pdfResult = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).QuotationClient.DownloadPdf(serialNumber);
    FileHelper.download(pdfResult);
  };

  const quotationMailForm = (request: ApiModels.InvoiceDefaultMailOptionsBaseRequest, serialNumber?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .QuotationClient.GetDefaultMailOptions(request, serialNumber)
      .then((data) => data.data);
  };

  const changeState = (serialNumber: number, newState: ApiModels.ModifiableQuotationState) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .QuotationClient.SetStatus(serialNumber, { modifiedState: newState } as ApiModels.ChangeStatusPost)
      .then((res) => res.data);
  };

  const createOrUpdateQuotation = async (
    serialNumber?: number,
    quotation?: ApiModels.QuotationPost
  ): Promise<number | undefined> => {
    let quotationSerialNumber = serialNumber;
    if (quotation)
      if (quotationSerialNumber) await updateQuotation(quotationSerialNumber, quotation);
      else {
        quotationSerialNumber = await createQuotation({ ...quotation });
      }

    return quotationSerialNumber;
  };

  const sendAsEmail = (mailPost: ApiModels.SendQuotationMailPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).QuotationClient.SendMail(mailPost);
  };

  const getStateLogs = (serialNumber: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .QuotationClient.GetStateLogs(serialNumber)
      .then((res) => res.data);
  };

  const deleteQuotation = (serialNumber: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).QuotationClient.Cancel(serialNumber);
  };

  const convertFormToAPIRequest = (form: InvoiceFormTypes.Create, serialNumber?: number): ApiModels.QuotationPost => {
    const houseWorkType = form.rutRot?.houseWorkType;
    const isRot = houseWorkType === ApiModels.InvoiceHouseWorkType.ROT;
    const isRut = houseWorkType === ApiModels.InvoiceHouseWorkType.RUT;
    const rutRotArticleIds = new Set((form.rutRot?.rutRotArticles ?? []).map((a) => String(a.id)));

    return {
      customerId: (form.customer?.id as number) ?? 0,
      customerReference: form.customer?.id
        ? {
            referenceId: form.referencePerson?.id as number,
            referenceName: form.referencePerson?.value,
          }
        : undefined,
      rows: form.products
        ? form.products.map((product, index): ApiModels.QuotationRow => {
            const isRutRotRow = product.uniqueId !== undefined && rutRotArticleIds.has(String(product.uniqueId));
            return {
              articleId: Number(product.article?.id ?? 0),
              orderIndex: index + 1,
              quantity: product.quantity,
              description: product.article?.value,
              overriddenDescription: product.text,
              rot: isRot && isRutRotRow,
              rut: isRut && isRutRotRow,
              price: product.price ?? 0,
              vAT: product.tax,
              overriddenPrice: product.price ?? 0,
              unitId: product.unit,
            };
          })
        : [],
      contactPerson: form.otherSettings.sellerName,
      notes: form.otherSettings.notes,
      dimensions:
        form.products && form.products[0]?.dimensions && form.products[0]?.dimensions?.length > 0
          ? form.products[0]?.dimensions
          : undefined,
      date: form.otherSettings.invoiceDate,
      endDate: form.otherSettings.dueDate,
      showCurrencyCode: !!form.otherSettings.showCurrencyCode,
      language: form.otherSettings.languageKey,
      currency: form.currency,
      attachments: form.otherSettings.attachments as ApiModels.QuotationAttachment[],
      pdfTemplate: form.otherSettings.invoiceLayout as ApiModels.QuotationPdfTemplate,
      serialNumber: serialNumber ?? 0,
      rotPercentageType: ApiModels.InvoiceRotPercentageType.Standard,
    };
  };

  const getStates = () => {
    return EnumHelper.getNumericItemValues(ApiModels.QuotationApiState).map((state) => ({
      id: state,
      value: getStateText(state),
    }));
  };

  return {
    getQuotes,
    getStateText,
    reportToAPIFilter,
    get,
    getCopiedQuotation,
    generateQuotationPreview,
    getQuotationPDFPreview,
    getUIDisplayState,
    checkQuotationExpired,
    updateDimensions,
    getQuotationDimensions,
    getDisplayState,
    getInvoiceTemplateFromQuotation,
    createQuotation,
    updateQuotation,
    markAsSent,
    downloadPdf,
    quotationMailForm,
    changeState,
    createOrUpdateQuotation,
    sendAsEmail,
    getStateLogs,
    deleteQuotation,
    convertFormToAPIRequest,
    getStates,
  };
}

export default useQuotesService;
